import { Component, Input, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { TicketInterface } from "../../../../interfaces/ticket-interface";
import { TicketMessageService } from "../../../../services/ticket-message.service";
import { TicketService } from "../../../../services/ticket.service";
import { UserService } from "../../../../../../../core/user/user.service";
import { Subject } from 'rxjs';
import { DatePipe, NgForOf, NgIf } from "@angular/common";
import { TranslocoPipe } from "@ngneat/transloco";
import { FormsModule } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { CreateMessageInterface } from "../../../../interfaces/message-interface";
import { LogicalFileSystem } from "@angular/compiler-cli";

@Component({
    selector   : 'app-ticket-conversation',
    templateUrl: './ticket-conversation.component.html',
    styleUrls  : ['./ticket-conversation.component.scss'],
    standalone : true,
    imports    : [
        DatePipe,
        TranslocoPipe,
        NgForOf,
        NgIf,
        FormsModule,
        MatIconModule,
        MatButtonModule
    ]
})

export class TicketConversationComponent implements OnChanges {
    @Input() ticket: TicketInterface | null = null;
    newMessage: string = '';
    user: any;
    
    isNewTicket: boolean = false;
    creationStep: number = 0; // 0: normal, 1: waiting for title, 2: waiting for description
    newTicketTitle: string = '';
    newTicketDescription: string = '';
    conversationBuffer: CreateMessageInterface[] = [];
    currentUserRole: string = '';
    
    private unsubscribeAll: Subject<any> = new Subject<any>();
    
    constructor(
        private ticketService: TicketService,
        private userService: UserService,
        private messageService: TicketMessageService,
    ) {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            this.user = JSON.parse(userStr);
            this.currentUserRole = this.user.role;
        }
    }
    
    get hasSupportMessages(): boolean {
        return this.ticket?.messages?.some(m => m.user_role !== 'editor') || false;
    }
    
    ngOnInit(): void {
    
    }
    
    ngOnChanges(changes: SimpleChanges): void {
        if (changes.ticket) {
            if (this.ticket && this.ticket.id === 'new-ticket') {
                this.isNewTicket = true;
                if (this.creationStep === 0) {
                    this.creationStep = 1;
                    this.conversationBuffer = [];
                }
            } else {
                this.isNewTicket = false;
                this.creationStep = 0;
            }
        }
    }
    
    sendMessage(): void {
        const trimmedMessage = this.newMessage.trim();
        if (!trimmedMessage) return;
        
        if (this.isNewTicket) {
            if (this.creationStep === 1) {
                this.newTicketTitle = trimmedMessage;
                this.pushUserMessage(trimmedMessage);
                this.pushSupportMessage("Please describe your issue in detail.");
                this.creationStep = 2;
                this.newMessage = '';
            } else if (this.creationStep === 2) {
                this.newTicketDescription = trimmedMessage;
                this.pushUserMessage(trimmedMessage);
                const ticketToCreate: TicketInterface = {
                    title      : this.newTicketTitle,
                    description: this.newTicketDescription,
                    user_id    : this.user ? this.user.id.toString() : ''
                } as TicketInterface;
                this.ticketService.create(ticketToCreate).subscribe({
                    next : (createdTicket: TicketInterface) => {
                        this.conversationBuffer.forEach(message => {
                            this.messageService.create(createdTicket.id, message).subscribe({
                                error: (err) => {
                                    console.error("Error sending message:", err);
                                }
                            });
                        });
                        this.ticketService.show(createdTicket.id).subscribe({
                            next : (updatedTicket: TicketInterface) => {
                                this.ticket = updatedTicket;
                                this.isNewTicket = false;
                                this.creationStep = 0;
                                this.conversationBuffer = [];
                            },
                            error: (err) => {
                                console.error("Error retrieving updated ticket:", err);
                            }
                        });
                    },
                    error: (err) => {
                        console.error("Error creating ticket:", err);
                    }
                });
                this.newMessage = '';
            }
        } else {
            this.pushUserMessage(trimmedMessage);
            this.newMessage = '';
        }
    }
    
    private pushUserMessage(content: string): void {
        if (this.ticket) {
            if (this.ticket.id === 'new-ticket') {
                const message: CreateMessageInterface = {
                    content: content,
                };
                const mockMessage = {
                    id        : 'temp-' + new Date().getTime(),
                    ticket_id : this.ticket.id,
                    user_id   : this.user.id.toString(),
                    user_role : this.user.role,
                    content   : content,
                    created_at: new Date()
                };
                this.ticket.messages.push(mockMessage);
                this.conversationBuffer.push(message);
                
            } else {
                const message: CreateMessageInterface = {
                    content: content,
                };
                this.messageService.create(this.ticket.id, message).subscribe({
                    next : (msg: any) => {
                        const tempMessage = {
                            id        : msg.id,
                            ticket_id : msg.ticket_id,
                            user_id   : msg.user.id,
                            user_role : msg.user.role,
                            content   : msg.content,
                            created_at: msg.created_at
                        }
                        this.ticket.messages.push(tempMessage);
                        
                    },
                    error: (err) => {
                        console.error("Error sending user message:", err);
                    }
                });
            }
        }
    }
    
    private pushSupportMessage(content: string): void {
        if (!this.ticket) return;
        
        const message: CreateMessageInterface = {
            content: content,
        };
        const tempMessage = {
            id        : 'temp-' + new Date().getTime(),
            ticket_id : this.ticket.id,
            user_id   : 'customer-support',
            user_role : 'super-admin',
            content   : content,
            created_at: new Date()
        };
        this.ticket.messages.push(tempMessage);
    }
}